import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Expand } from "lucide-react";
import { useApartments } from "../hooks/useApartments";
import { Lightbox } from "./Lightbox";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const ApartmentImage = ({ apt, rounded, onOpen }) => {
  const [current, setCurrent] = useState(apt.image);
  useEffect(() => {
    setCurrent(apt.image);
  }, [apt.image]);
  const shots = [apt.image, ...(apt.gallery || [])];
  return (
    <div>
      <button
        type="button"
        onClick={() => onOpen(shots.indexOf(current))}
        data-testid={`apartment-open-lightbox-${apt.id}`}
        className={`group relative block w-full overflow-hidden ${rounded} border border-[#C29B62]/20 cursor-zoom-in`}
      >
        <img
          src={current}
          alt={apt.name}
          loading="lazy"
          data-testid={`apartment-image-${apt.id}`}
          className="w-full h-72 lg:h-[26rem] object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <span className="absolute bottom-4 right-4 p-2 bg-[#101f17]/60 text-[#F7F5F0] opacity-0 group-hover:opacity-100 transition-opacity">
          <Expand className="w-4 h-4" />
        </span>
      </button>
      {shots.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-3" data-testid={`apartment-gallery-${apt.id}`}>
          {shots.map((src, i) => (
            <button
              key={i}
              type="button"
              data-testid={`apartment-thumb-${apt.id}-${i}`}
              onClick={() => setCurrent(src)}
              className={`overflow-hidden border transition-colors ${
                current === src ? "border-[#1C3829]" : "border-[#C29B62]/25 hover:border-[#C29B62]"
              }`}
            >
              <img src={src} alt={`${apt.name} ${i + 1}`} loading="lazy" className="w-16 h-12 lg:w-20 lg:h-14 object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Apartments = ({ t, lang }) => {
  const APARTMENTS = useApartments();
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="appartamenti" data-testid="apartments-section" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.35em] text-[#C29B62]">{t.apartments.overline}</p>
          <h2 className="mt-4 text-4xl sm:text-5xl font-light tracking-tight text-[#1C3829]">{t.apartments.title}</h2>
          <p className="mt-6 text-base text-[#4A4A4A] font-light leading-relaxed">{t.apartments.sub}</p>
        </motion.div>

        <div className="mt-16 space-y-20 lg:space-y-28">
          {APARTMENTS.map((apt, i) => (
            <motion.article
              key={apt.id}
              data-testid={`apartment-card-${apt.id}`}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className={`flex flex-col ${i % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"} gap-10 lg:gap-16 items-center`}
            >
              <div className="w-full lg:w-3/5">
                <ApartmentImage
                  apt={apt}
                  rounded={i % 2 === 0 ? "rounded-t-[10rem]" : "rounded-none"}
                  onOpen={(index) =>
                    setLightbox({ shots: [apt.image, ...(apt.gallery || [])], index, name: apt.name })
                  }
                />
              </div>
              <div className={`w-full lg:w-2/5 ${i % 2 === 1 ? "lg:text-right" : ""}`}>
                <span className="font-serif-display text-6xl font-light text-[#C29B62]/40">0{i + 1}</span>
                <h3 className="mt-2 text-3xl font-light tracking-tight text-[#1A1A1A]">{apt.name}</h3>
                <p className="mt-4 text-base text-[#4A4A4A] font-light leading-relaxed">{lang === "it" ? apt.it : apt.en}</p>
                <div className={`mt-6 flex items-center gap-6 text-sm text-[#1A1A1A]/70 ${i % 2 === 1 ? "lg:justify-end" : ""}`}>
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#C29B62]" />
                    {apt.guests} {t.apartments.guests}
                  </span>
                  <span>
                    {t.apartments.from} <strong className="text-[#1C3829]">€{apt.pricing.low}</strong> / {t.apartments.night}
                  </span>
                </div>
                <Link
                  data-testid={`apartment-book-button-${apt.id}`}
                  to="/prenota"
                  onClick={() => sessionStorage.setItem("vn_apartment", apt.id)}
                  className="mt-8 inline-block bg-[#1C3829] text-[#F7F5F0] text-xs uppercase tracking-[0.25em] px-8 py-4 hover:text-[#C29B62] hover:-translate-y-0.5 transition-all duration-300"
                >
                  {t.apartments.book}
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
      {lightbox && (
        <Lightbox
          lightbox={lightbox}
          onClose={() => setLightbox(null)}
          onNav={(index) => setLightbox({ ...lightbox, index })}
        />
      )}
    </section>
  );
};
